/**
 * The parts of a profile that are the same whether it is yours or somebody else's.
 *
 * Your own screen has edit controls on it — a picture to change, a category to set, a way
 * out — and another player's has none of that. Everything below the header is the same
 * question about a different person, so it lives here and both screens read it.
 */
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "./Avatar.js";
import { RatingHistory } from "./RatingHistory.js";
import { RatingLockup, Tally } from "./RatingLockup.js";
import { fetchAchievements } from "../lib/achievements.js";
import { fetchRatingHistory, type DisciplineRating } from "../lib/ratings.js";
import { SPORT_LABEL } from "../lib/sport.js";
import type { PlayerTally } from "../lib/stats.js";
import type { Kind } from "../lib/types.js";

/**
 * A section's heading: a mono label on the left, an optional aside on the right (a column
 * head, a toggle, a link), and the tramline under both — the same head every column of the
 * scoresheet sits under.
 */
export function SectionHead({ title, aside }: { title: string; aside?: ReactNode }) {
  return (
    <div className="mb-0">
      <div className="font-mono flex min-h-[28px] items-end justify-between gap-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.12em]">
        <h2>{title}</h2>
        {aside ? <div className="text-ink-muted">{aside}</div> : null}
      </div>
      <div className="tramline" aria-hidden />
    </div>
  );
}

/**
 * The events this person was MVP of, one line each, newest tournament first, each a link to the
 * tournament. Nothing at all when there are none: a profile with no achievements has no heading
 * promising some.
 *
 * What is returned follows who may see the tournament, so an MVP of a private tournament shows
 * it to its invitees and organisers and to nobody else — this component filters nothing itself.
 */
export function Achievements({ memberId }: { memberId: string }) {
  const { data: achievements = [] } = useQuery({
    queryKey: ["achievements", memberId],
    queryFn: () => fetchAchievements(memberId),
    enabled: memberId !== "",
  });

  if (achievements.length === 0) return null;

  return (
    <section className="mt-8" data-testid="achievements">
      <SectionHead title="Achievements" />
      <ul>
        {achievements.map((achievement) => (
          <li
            key={achievement.eventId}
            className="border-b"
            style={{ borderColor: "var(--color-line-muted)" }}
          >
            <Link
              to={`/tournaments/${achievement.tournamentId}`}
              className="flex min-h-[54px] items-center gap-2.5 hover:underline"
            >
              <span className="font-mono shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em]">
                MVP
              </span>
              <span className="flex-1 truncate text-[15px] font-semibold">
                {achievement.eventName}, {achievement.tournamentName}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The person's name as the scoresheet's header: tall, condensed, upper case. */
export function ProfileName({ name }: { name: string }) {
  return (
    <h1
      className="font-display uppercase"
      style={{ fontSize: "clamp(40px, 13vw, 50px)", fontWeight: 900, lineHeight: 0.84 }}
    >
      {name}
    </h1>
  );
}

/**
 * The record, as a row of the scoresheet: heavy rules top and bottom, a hairline between
 * each figure, the figure in the display face and its label in mono.
 */
export function Stats({ items }: { items: { value: string; label: string }[] }) {
  return (
    <section
      aria-label="Record"
      className="mt-7 grid border-y-2"
      style={{
        borderColor: "var(--color-line)",
        gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
      }}
    >
      {items.map((item, i) => (
        <div
          key={item.label}
          className={`pb-2.5 pt-3 ${i > 0 ? "border-l pl-3" : ""}`}
          style={i > 0 ? { borderColor: "var(--color-line-muted)" } : undefined}
        >
          <div className="font-display text-[34px] leading-[0.9]" style={{ fontWeight: 800 }}>
            {item.value}
          </div>
          <div className="font-mono mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-muted">
            {item.label}
          </div>
        </div>
      ))}
    </section>
  );
}

/**
 * The last ten results, read left to right like a scorebook: oldest first, latest last.
 * Won is a filled square with a W and lost an outlined one with an L — shape and letter
 * before colour, the same rule the rating lockup's arrow follows.
 */
export function Form({ results }: { results: boolean[] }) {
  if (results.length === 0) return null;

  // `results` arrives most recent first; the row reads the other way.
  const inOrder = [...results].reverse();

  return (
    <section className="mt-7">
      <h2 className="font-mono mb-2.5 text-[11px] font-semibold uppercase tracking-[0.12em]">
        Form · oldest → latest
      </h2>
      <ol
        className="flex flex-wrap gap-1"
        aria-label={`Last ${results.length} results, oldest first: ${inOrder
          .map((won) => (won ? "won" : "lost"))
          .join(", ")}`}
      >
        {inOrder.map((won, i) => (
          <li
            key={i}
            aria-hidden
            className="font-display grid size-[30px] place-items-center text-base"
            style={{
              fontWeight: 800,
              border: "1.5px solid var(--color-line)",
              background: won ? "var(--color-line)" : "transparent",
              color: won ? "var(--color-ground)" : "var(--color-ink)",
            }}
          >
            {won ? "W" : "L"}
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Both disciplines at once, because the first question anyone asks a profile is which of
 * the two they are better at, and an answer you have to scroll for is a worse answer. That
 * is what the `card` size exists for.
 */
export function RatingCards({ ratings }: { ratings: DisciplineRating[] }) {
  if (ratings.length === 0) return null;

  return (
    <section className="mt-6">
      <h2 className="sr-only">Rating · {SPORT_LABEL}</h2>
      {/* One scorecard, two columns, a rule between: the first question anyone asks their
          profile is which of the two they are better at. */}
      <div className="grid grid-cols-2 border-2" style={{ borderColor: "var(--color-line)" }}>
        {(["doubles", "singles"] as Kind[]).map((kind, index) => {
          const rating = ratings.find((r) => r.kind === kind);
          return (
            <div
              key={kind}
              className="min-w-0 px-3 pb-3.5 pt-3"
              style={{
                borderLeft: index === 0 ? undefined : "2px solid var(--color-line)",
              }}
            >
              {rating ? (
                <RatingLockup
                  r={rating.r}
                  rd={rating.rd}
                  label={kind}
                  matchesPlayed={rating.matchesPlayed}
                  size="card"
                />
              ) : (
                <Unplayed kind={kind} />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * A discipline nobody has played. Both slots always render, because an empty half of the
 * grid reads as a rating that failed to load — and this community plays doubles, so for
 * most people the singles slot is permanently empty and permanently fine.
 */
function Unplayed({ kind }: { kind: Kind }) {
  return (
    <div>
      <div className="micro mb-2">{kind}</div>
      <div
        className="font-display"
        style={{ fontSize: 64, fontWeight: 900, lineHeight: 0.8, color: "var(--color-line-muted)" }}
      >
        &ndash;
      </div>
      <Tally confidence={0} />
      <div className="font-mono mt-2.5 text-xs text-ink-muted">no matches yet</div>
    </div>
  );
}

/**
 * Rating over time for one discipline, with a switcher when there is more than one.
 *
 * The discipline shown is the one they actually play, not an alphabetical default: nothing
 * is fetched until the ratings say which that is, so the chart never asks for a history of
 * a discipline nobody has played.
 */
export function RatingTrend({
  memberId,
  ratings,
}: {
  memberId: string;
  ratings: DisciplineRating[];
}) {
  const [chosen, setChosen] = useState<Kind | null>(null);
  const busiest = [...ratings].sort((a, b) => b.matchesPlayed - a.matchesPlayed)[0]?.kind ?? null;
  const shown = chosen ?? busiest;

  const { data: history = [] } = useQuery({
    queryKey: ["rating-history", memberId, shown],
    queryFn: () => fetchRatingHistory(memberId, shown!),
    enabled: shown !== null,
  });

  if (!shown) return null;

  return (
    <section className="mt-8">
      <SectionHead
        title={
          history.length > 1
            ? `${shown} · last ${history.length} matches`
            : `${shown} · rating over time`
        }
        aside={
          ratings.length > 1 ? (
            <span className="flex" role="group" aria-label="Discipline">
              {ratings.map((rating) => (
                <button
                  key={rating.kind}
                  type="button"
                  onClick={() => setChosen(rating.kind)}
                  aria-pressed={rating.kind === shown}
                  className="px-2.5 uppercase"
                  style={{
                    minHeight: 32,
                    border: "1.5px solid var(--color-line)",
                    marginLeft: -1.5,
                    background: rating.kind === shown ? "var(--color-line)" : "transparent",
                    color: rating.kind === shown ? "var(--color-ground)" : "var(--color-ink)",
                  }}
                >
                  {rating.kind}
                </button>
              ))}
            </span>
          ) : null
        }
      />
      <div className="mt-3">
        <RatingHistory points={history} />
      </div>
    </section>
  );
}

/**
 * Who someone plays with, or against, and how it goes. Requirements §5.1 calls the partner
 * table the most-viewed screen in comparable apps, and it costs nothing — the matches are
 * already here for the list below it.
 *
 * Every row is a way into that person's profile, which is the whole point: partner tables
 * are how you end up reading about somebody you have never met.
 */
export function PeopleTable({
  title,
  people,
  empty,
  limit = 5,
}: {
  title: string;
  people: PlayerTally[];
  empty: string;
  limit?: number;
}) {
  return (
    <section className="mt-8">
      <SectionHead title={title} aside={people.length > 0 ? "W–L" : undefined} />
      {people.length === 0 ? (
        <p className="mt-3 text-sm text-ink-muted">{empty}</p>
      ) : (
        <ul>
          {people.slice(0, limit).map((person) => (
            <li
              key={person.memberId}
              className="border-b"
              style={{ borderColor: "var(--color-line-muted)" }}
            >
              <Link
                to={`/player/${person.memberId}`}
                className="flex min-h-[54px] items-center gap-2.5"
              >
                <Avatar name={person.displayName} url={person.avatarUrl} size={30} />
                <span className="flex-1 truncate text-[15px] font-semibold">
                  {person.displayName}
                </span>
                <span className="font-mono shrink-0 text-sm">
                  {person.won}&ndash;{person.lost}
                </span>
                <span className="font-mono w-12 shrink-0 text-right text-xs text-ink-muted">
                  {Math.round((person.won / person.played) * 100)}%
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
